<template>
  <div class="sf-el scheduling">
    <div class="scheduling__header">
      <div class="scheduling__header-top">
        <span class="scheduling__header-eyebrow">{{ label }} · {{ slotLengthMinutes }} min</span>
      </div>
      <h3 class="scheduling__header-title">{{ stepTitle }}</h3>
      <p v-if="stepSubtitle" class="scheduling__header-subtitle">
        {{ stepSubtitle }}
        <strong v-if="stepSubtitleHighlight">{{ stepSubtitleHighlight }}</strong>
      </p>
      <ul v-if="headerChips.length" class="scheduling__header-chips">
        <li v-for="chip in headerChips" :key="chip.icon" class="scheduling__header-chip">
          <BaseIcon :name="chip.icon" size="16" />
          {{ chip.text }}
        </li>
      </ul>
    </div>

    <!-- Location stays mounted while slots reload, so an open address form is not reset. -->
    <div v-if="hasLocations" v-show="showLocationStep" class="scheduling__location">
      <!-- Step 1: location type (only when there is more than one type to choose from) -->
      <div
        v-if="availableLocationKinds.length > 1"
        class="scheduling__kind-group"
        role="radiogroup"
        :aria-label="t('form.fields.scheduling.select_location')"
      >
        <label
          v-for="kind in availableLocationKinds"
          :key="kind"
          class="scheduling__kind-card"
          :class="{ 'scheduling__kind-card--selected': selectedLocationKind === kind }"
        >
          <input
            class="scheduling__radio-input"
            type="radio"
            name="scheduling-location-kind"
            :value="kind"
            :checked="selectedLocationKind === kind"
            @change="selectLocationKind(kind)"
          />
          <span class="scheduling__kind-icon">
            <BaseIcon :name="LOCATION_KIND_ICONS[kind]" size="18" />
          </span>
          <span class="scheduling__kind-text">
            <span class="scheduling__kind-title">
              {{ t(`form.fields.scheduling.location_type_${kind}`) }}
            </span>
            <span class="scheduling__kind-desc">
              {{ t(`form.fields.scheduling.location_type_${kind}_desc`) }}
            </span>
          </span>
          <span class="scheduling__radio-dot" aria-hidden="true"></span>
        </label>
      </div>

      <!-- Step 2a: several locations of the same non-fixed type -> plain select -->
      <select
        v-if="showKindSelect"
        v-model="selectedLocationId"
        class="scheduling__location-select"
        @change="handleLocationChange"
      >
        <option value="" disabled>
          {{ t('form.fields.scheduling.choose_location') }}
        </option>
        <option v-for="location in kindLocations" :key="location.id" :value="location.id">
          {{ location.name }}
        </option>
      </select>

      <!-- Step 2b: fixed locations as radio cards -->
      <h4 v-if="showFixedLocationList" class="scheduling__fixed-list-title">
        {{ t('form.fields.scheduling.available_locations') }}
      </h4>
      <div
        v-if="showFixedLocationList"
        class="scheduling__fixed-list"
        role="radiogroup"
        :aria-label="t('form.fields.scheduling.available_locations')"
      >
        <label
          v-for="location in locationsByKind.fixed"
          :key="location.id"
          class="scheduling__fixed-card"
          :class="{ 'scheduling__fixed-card--selected': selectedLocationId === location.id }"
        >
          <input
            v-model="selectedLocationId"
            class="scheduling__radio-input"
            type="radio"
            name="scheduling-fixed-location"
            :value="location.id"
            @change="handleLocationChange"
          />
          <span class="scheduling__radio-dot" aria-hidden="true"></span>
          <span class="scheduling__fixed-text">
            <span class="scheduling__fixed-name">{{ location.name }}</span>
            <span v-if="formatLocationAddress(location)" class="scheduling__fixed-address">
              {{ formatLocationAddress(location) }}
            </span>
          </span>
        </label>
      </div>

      <!-- Custom address input (when location type is 'custom') -->
      <div v-if="selectedLocationType === 'custom'" class="scheduling__custom-address">
        <AddressElement
          :label="t('form.fields.scheduling.enter_address') || 'Vul je adres in'"
          :required="true"
          :modelValue="customAddress"
          @update:modelValue="handleCustomAddressUpdate"
          @view-state="handleAddressViewState"
          mode="smart"
          :showAddressCheck="true"
        />
        <div v-if="addressError" class="scheduling__address-error" role="alert">
          {{ addressError }}
        </div>
      </div>

      <div v-if="locationStepNeeded" class="scheduling__step-actions">
        <Btn
          variant="primary"
          size="md"
          type="button"
          :disabled="!selectedLocationId || loading"
          :loading="loading"
          @click="goToAppointmentStep"
        >
          {{ t('form.fields.scheduling.next_step') }}
        </Btn>
      </div>
    </div>

    <template v-if="!loading && showAppointmentStep">
      <div v-if="error" class="scheduling__error" role="alert">
        {{ error }}
      </div>
      <template v-if="canShowBookingUi">
        <div class="scheduling__booking-ui">
          <div v-if="showBookingConfirm" class="scheduling__confirm">
            <div class="scheduling__confirm-details">
              <fieldset
                class="scheduling__confirm-contact"
                :disabled="hasConfirmedBooking || isReserving"
              >
                <NameElement
                  v-bind="nameFieldProps"
                  :modelValue="nameFieldValue"
                  @update:modelValue="onNameFieldUpdate"
                />
                <EmailElement
                  v-bind="emailFieldProps"
                  :modelValue="emailFieldValue"
                  @update:modelValue="onEmailFieldUpdate"
                />
              </fieldset>
              <p v-if="bookingDetailsError" class="scheduling__confirm-error" role="alert">
                {{ bookingDetailsError }}
              </p>
              <div class="scheduling__confirm-actions">
                <Btn
                  v-if="!hasConfirmedBooking"
                  variant="outlined"
                  type="button"
                  :disabled="isReserving"
                  @click="backFromBookingConfirm"
                >
                  {{ t('form.fields.scheduling.booking_back') }}
                </Btn>
                <Btn
                  v-if="!hasConfirmedBooking || takesOverSubmit"
                  variant="primary"
                  size="md"
                  type="button"
                  class="scheduling__confirm-submit"
                  :loading="isReserving || formIsSubmitting"
                  :disabled="isReserving || formIsSubmitting"
                  @click="confirmBooking"
                >
                  {{ t('form.fields.scheduling.booking_schedule_event') }}
                </Btn>
              </div>
            </div>
          </div>
          <template v-else>
          <div v-if="showNoCoveragePin" class="scheduling__pin-cards">
            <p class="scheduling__no-coverage-notice" role="status">
              {{ t('form.fields.scheduling.out_of_service_area') }}
            </p>
            <article class="scheduling__pin-card scheduling__pin-card--unavailable">
              <div class="scheduling__pin-card-body">
                <div class="scheduling__pin-card-slot">
                  <p class="scheduling__pin-card-intro">
                    {{ t('form.fields.scheduling.suggestion_availability_from') }}
                  </p>
                  <p class="scheduling__pin-card-date scheduling__pin-card-date--unavailable">—</p>
                </div>
                <Btn
                  variant="primary"
                  size="md"
                  class="scheduling__pin-card-book scheduling__pin-card-book--cta"
                  disabled
                >
                  {{ t('form.fields.scheduling.book_suggested_slot') }}
                </Btn>
              </div>
            </article>
          </div>
          <div v-else-if="showPinCards" class="scheduling__pin-cards">
            <button
              v-for="pin in suggestionPins"
              :key="pin.slotId"
              type="button"
              class="scheduling__slot-card"
              :disabled="slotsLocked || isReserving"
              @click="bookSuggestedSlot(pin)"
            >
              <span class="scheduling__slot-badge" aria-hidden="true">
                <span class="scheduling__slot-badge-month">{{ pin.monthLabel }}</span>
                <span class="scheduling__slot-badge-day">{{ pin.dayLabel }}</span>
              </span>
              <span class="scheduling__slot-text">
                <span class="scheduling__slot-title">{{ pin.titleLabel }}</span>
                <span class="scheduling__slot-subtitle">{{ pin.kindLabel }}</span>
              </span>
              <span class="scheduling__slot-time">
                <BaseIcon name="timer" size="16" />
                {{ pin.clockLabel }}
              </span>
              <BaseIcon name="chevron" size="14" class="scheduling__slot-chevron" />
            </button>
            <button
              v-if="!showFullCalendar"
              type="button"
              class="scheduling__pin-card-choose-own"
              :disabled="slotsLocked"
              @click="openFullCalendar"
            >
              {{ t('form.fields.scheduling.choose_own_time') }}
              <BaseIcon name="chevron" size="14" />
            </button>
          </div>
          <div v-if="showCalendarPicker" class="scheduling__calendar-panel">
            <div v-if="showPinCards" class="scheduling__calendar-panel-header">
              <div>
                <p class="scheduling__calendar-panel-title">
                  {{ t('form.fields.scheduling.choose_availability_title') }}
                </p>
                <p class="scheduling__calendar-panel-hint">
                  {{ t('form.fields.scheduling.choose_availability_hint') }}
                </p>
              </div>
              <button
                type="button"
                class="scheduling__calendar-panel-close"
                @click="closeFullCalendar"
              >
                {{ t('form.fields.scheduling.close_calendar') }}
                <BaseIcon name="xmark" size="14" />
              </button>
            </div>
            <div class="scheduling__calendar-view">
        <div class="scheduling__month">
          <span class="scheduling__month-label">{{ selectedMonth }}</span>
          <div class="scheduling__month-actions">
            <button
              type="button"
              class="scheduling__today-button"
              :disabled="loading"
              @click="goToToday"
            >
              {{ t('form.fields.scheduling.go_to_today') }}
            </button>
            <div class="scheduling__month-nav">
              <button
                class="scheduling__nav-button scheduling__nav-button--prev"
                :disabled="!canGoBack"
                @click="goToPreviousMonth"
                type="button"
              >
                <BaseIcon name="chevron" size="16" />
              </button>
              <button
                class="scheduling__nav-button scheduling__nav-button--next"
                @click="goToNextMonth"
                type="button"
              >
                <BaseIcon name="chevron" size="16" />
              </button>
            </div>
          </div>
        </div>
        <div class="scheduling__picker">
          <div class="scheduling__calendar" ref="calendarRef">
            <div class="scheduling__weekdays">
              <div
                v-for="(weekday, i) in ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']"
                :key="weekday + i"
                class="scheduling__weekday"
              >
                {{ t(`form.fields.scheduling.weekdays_short.${weekday}`) }}
              </div>
            </div>
            <div class="scheduling__days-grid">
              <template v-for="(cell, idx) in calendarCells" :key="idx">
                <div
                  v-if="cell"
                  class="scheduling__day-circle"
                  :class="{
                    'scheduling__day-circle--has-slots': cell.slotsCount > 0,
                    'scheduling__day-circle--selected':
                      selectedDay && selectedDay.date === cell.date,
                    'scheduling__day-circle--empty': cell.slotsCount === 0,
                    'scheduling__day-circle--today': cell.date === todayIsoDate,
                  }"
                  :data-slots-count="cell.slotsCount"
                  @click="cell.slotsCount > 0 && selectDay(cell)"
                >
                  <span>{{ cell.dayNumber }}</span>
                </div>
                <div
                  v-else
                  class="scheduling__day-circle scheduling__day-circle--placeholder"
                ></div>
              </template>
            </div>
          </div>

          <div
            v-if="selectedDay"
            class="scheduling__times"
            :style="calendarHeight ? { maxHeight: calendarHeight + 'px' } : {}"
          >
            <div class="scheduling__times-header">{{ formattedSelectedDate }}</div>
            <div
              v-for="(slot, index) in selectedDay.slots"
              :key="index"
              class="scheduling__time-slot"
              @click="selectTimeSlot(slot)"
              :class="{
                'scheduling__time-slot--selected': selectedSlot && selectedSlot.id === slot.id,
                'scheduling__time-slot--disabled': slotsLocked,
              }"
            >
              {{ formatToTimeZone(slot, true) }}
              <!-- Show availability count for Round Robin -->
              <span v-if="slot.integrationCount > 1" class="scheduling__slot-count">
                ({{ slot.integrationCount }})
              </span>
            </div>
          </div>
        </div>
            </div>
          </div>
          </template>
        </div>
        <div v-if="!showNoCoveragePin && !showBookingConfirm" class="scheduling__footer">
          <template v-if="selectedSlot">
            <span>{{ t('form.fields.scheduling.selected_slot') + ' ' }}</span>
            <span>{{ formatToTimeZone(selectedSlot) }}</span>
          </template>
          <template v-else>{{ t('form.fields.scheduling.no_slot_selected') }}</template>
        </div>
      </template>
      <!-- Show "choose location first" when location required but not selected -->
      <div
        v-else-if="requireLocation && hasLocations && !selectedLocationId"
        class="scheduling__choose-location"
      >
        {{ t('form.fields.scheduling.choose_location_first') || 'Kies eerst een locatie' }}
      </div>
      <div v-else-if="!awaitingCustomAddress" class="scheduling__no-slots">
        {{ t('form.fields.scheduling.no_slots') }}
      </div>
    </template>

    <!-- Step 2 -> step 1, same button as the back button of the last step -->
    <div
      v-if="showBackToLocation && !loading"
      class="scheduling__confirm-actions scheduling__confirm-actions--step"
    >
      <Btn variant="outlined" type="button" @click="backToLocationStep">
        {{ t('form.fields.scheduling.booking_back') }}
      </Btn>
    </div>

    <div v-if="loading && showAppointmentStep" class="scheduling__loading">
      <LoadingSpinner size="lg" color="dark" />
      <span>{{ t('form.fields.scheduling.loading') }}</span>
    </div>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import localizedFormat from 'dayjs/plugin/localizedFormat';
  import 'dayjs/locale/nl';
  import 'dayjs/locale/en';
  import {
    ref,
    onMounted,
    onUnmounted,
    computed,
    watch,
    watchEffect,
    nextTick,
    inject,
  } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@shared/components/Btn.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';
  import AddressElement from '@shared/components/form/elements/AddressElement.vue';
  import NameElement from '@shared/components/form/elements/NameElement.vue';
  import EmailElement from '@shared/components/form/elements/EmailElement.vue';
  import { sessionService } from '@forms/services/sessionService.js';
  import { useI18n } from '@shared/composables/useI18n';

  const getSchedulingService = async (forceDummy = false) => {
    // Check if we're in Nuxt or if env variable is set; the builder preview always uses the dummy
    const useRealService =
      !forceDummy &&
      (typeof useRuntimeConfig === 'function'
        ? useRuntimeConfig()?.public?.useRealScheduling
        : false);

    if (useRealService) {
      const { schedulingService } = await import('@shared/services/schedulingService');
      return schedulingService;
    }
    // Dummy service implementation with Round Robin support
    return {
      getLocations: async () => {
        return {
          locations: [],
          requireLocation: false,
          isRoundRobin: false,
        };
      },
      getAvailableSlots: async (formId, fieldId, language, locationId, month) => {
        const monthStart = (month ? dayjs(`${month}-01`) : dayjs()).startOf('month');
        const today = dayjs().startOf('day');
        // Like the real API: every day of the month, with slots on upcoming weekdays only
        const days = [];
        for (let d = 0; d < monthStart.daysInMonth(); d++) {
          const day = monthStart.add(d, 'day');
          const isBookable = !day.isBefore(today) && day.day() !== 0 && day.day() !== 6;
          const slots = isBookable
            ? [10, 14].map((hour) => ({
                id: `dummy-slot-${day.format('YYYY-MM-DD')}-${hour}`,
                startTime: day.add(hour, 'hour').toISOString(),
                endTime: day.add(hour + 1, 'hour').toISOString(),
                status: 'available',
                integrationCount: 1,
              }))
            : [];
          days.push({
            date: day.format('YYYY-MM-DD'),
            dayName: day.format('ddd'),
            dayNumber: day.date(),
            slotsCount: slots.length,
            slots,
          });
        }
        // Same shape as the API's suggestions.list, so the suggestion cards render too
        const bookableDays = days.filter((day) => day.slotsCount > 0);
        const list = [
          [0, 0, 'route'],
          [1, 0, 'morning'],
          [2, 1, 'afternoon'],
        ]
          .map(([dayIdx, slotIdx, kind]) => {
            const slot = bookableDays[dayIdx]?.slots[slotIdx];
            return slot
              ? { slotId: slot.id, startTime: slot.startTime, endTime: slot.endTime, kind }
              : null;
          })
          .filter(Boolean);
        return {
          months: [{ name: monthStart.format('MMMM YYYY'), days }],
          suggestions: { list },
          currentMonth: monthStart.format('MMMM YYYY'),
          requestedMonth: month || monthStart.format('YYYY-MM'),
          hasMore: true,
          isRoundRobin: false,
          selectedLocationId: locationId,
        };
      },
      reserveSlot: async (formId, slotId, submissionId, locationId) => {
        return {
          message: 'Slot reserved successfully',
          expiresIn: 900,
          slot: {
            slot_id: slotId,
            start_time: new Date().toISOString(),
            end_time: new Date().toISOString(),
          },
        };
      },
    };
  };

  export default {
    name: 'SchedulingElement',
    components: {
      BaseIcon,
      Btn,
      LoadingSpinner,
      AddressElement,
      NameElement,
      EmailElement,
    },
    props: {
      formId: {
        type: String,
        required: true,
      },
      fieldId: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        default: '30 minute call',
      },
      caption: {
        type: String,
        default: "Let's schedule a quick call",
      },
      modelValue: {
        type: Object,
        default: () => ({}),
      },
      submissionId: {
        type: [String, null],
        default: null,
      },
      element: {
        type: Object,
        default: () => ({}),
      },
      // Builder preview: dummy slots and no draft submission
      dummy: { type: Boolean, default: false },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const { locale, messages, t } = useI18n();

      const daysContainer = ref(null);
      const calendarRef = ref(null);
      const schedulingService = ref(null);
      const registerFieldValidator = inject('registerFieldValidator', null);
      const unregisterFieldValidator = inject('unregisterFieldValidator', null);
      const clearFieldError = inject('clearFieldError', null);
      const formDataRef = inject('formData', null);
      const formElementsRef = inject('formElements', null);
      const setSubmitTakeover = inject('setSubmitTakeover', null);
      const requestFormAdvance = inject('requestFormAdvance', null);
      const formIsSubmitting = inject('formIsSubmitting', null);

      const emptyNameValue = () => ({ first_name: '', last_name: '' });
      const localNameValue = ref(emptyNameValue());
      const localEmailValue = ref('');
      const loading = ref(false);
      const isReserving = ref(false);
      const error = ref(null);

      // Location state
      const locations = ref([]);
      const selectedLocationId = ref('');
      const requireLocation = ref(false);
      const isRoundRobin = ref(false);
      const noCoverage = ref(false);
      const customAddress = ref({
        houseNumber: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      });

      // Calendar state
      const months = ref([]);
      const suggestions = ref(null);
      const showFullCalendar = ref(false);
      const selectedMonth = ref(null);
      const currentYearMonth = ref(null);
      const selectedDay = ref(null);
      const timeSlots = ref([]);
      const selectedSlot = ref(null);
      const pendingSlot = ref(null);
      const bookingStep = ref(null);
      const bookingDetailsError = ref('');
      const calendarHeight = ref(null);

      const slotLengthMinutes = computed(
        () => props.element?.props?.calendar?.slot_length || 30
      );

      const hasConfirmedBooking = computed(() => {
        const value = props.modelValue;
        return typeof value === 'string' && value.trim().length > 0;
      });

      const showBookingConfirm = computed(
        () => bookingStep.value === 'confirm' && !!pendingSlot.value
      );

      // The scheduler's own button advances/submits the form, so the default
      // Next/Submit button is hidden whenever this element is on the page.
      const takesOverSubmit = computed(() => !!requestFormAdvance);

      watchEffect(() => {
        if (typeof setSubmitTakeover === 'function') {
          setSubmitTakeover(props.fieldId, takesOverSubmit.value);
        }
      });

      // The form clears the slot when submit fails on it (taken/expired): go back
      // to picking a new time.
      watch(
        () => props.modelValue,
        async (value, previous) => {
          if (previous && !value && showBookingConfirm.value) {
            resetBookingConfirm();
            selectedSlot.value = null;
            await fetchAvailableSlots();
          }
        }
      );

      const confirmDateTimeLabel = computed(() => {
        const slot = pendingSlot.value || selectedSlot.value;
        if (!slot?.startTime) return '';
        const localeShort = (locale.value || 'en').split('-')[0];
        return dayjs(slot.startTime).locale(localeShort).format('D MMMM YYYY HH:mm');
      });

      const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      const nameFormElement = computed(() => {
        const elements = formElementsRef?.value;
        if (!Array.isArray(elements)) return null;
        return elements.find((el) => el.id === 'name-input' || el.type === 'name') || null;
      });

      const emailFormElement = computed(() => {
        const elements = formElementsRef?.value;
        if (!Array.isArray(elements)) return null;
        return elements.find((el) => el.id === 'email-input' || el.type === 'email') || null;
      });

      // Read and write the form's own name/email element when it has one. The fixed ids
      // are only a fallback; the API accepts them for scheduling forms without such elements.
      const nameFieldId = computed(() => nameFormElement.value?.id || 'name-input');
      const emailFieldId = computed(() => emailFormElement.value?.id || 'email-input');

      const nameUsesSplitFields = computed(
        () => nameFormElement.value?.props?.splitNames === true
      );

      const nameFieldProps = computed(() => {
        const propsFromForm = nameFormElement.value?.props || {};
        return {
          label: propsFromForm.label || t('form.fields.scheduling.booking_name_label'),
          caption: propsFromForm.caption || '',
          placeholder: propsFromForm.placeholder,
          firstNamePlaceholder: propsFromForm.firstNamePlaceholder,
          lastNamePlaceholder: propsFromForm.lastNamePlaceholder,
          required: true,
          splitNames: nameUsesSplitFields.value,
        };
      });

      const emailFieldProps = computed(() => {
        const propsFromForm = emailFormElement.value?.props || {};
        return {
          label: propsFromForm.label || t('form.fields.scheduling.booking_email_label'),
          caption: propsFromForm.caption || '',
          placeholder:
            propsFromForm.placeholder || t('form.fields.scheduling.booking_email_placeholder'),
          required: true,
        };
      });

      function readNameFromFormData(data) {
        if (!data || typeof data !== 'object') return null;
        const raw = data[nameFieldId.value];
        if (nameUsesSplitFields.value) {
          if (raw && typeof raw === 'object') {
            return {
              first_name: raw.first_name || '',
              last_name: raw.last_name || '',
            };
          }
          return emptyNameValue();
        }
        if (typeof raw === 'string') return raw;
        if (raw && typeof raw === 'object') {
          const full = [raw.first_name, raw.last_name].filter(Boolean).join(' ').trim();
          return full || '';
        }
        return '';
      }

      function writeNameToFormData(data, value) {
        if (!data || typeof data !== 'object') return;
        if (nameUsesSplitFields.value) {
          const obj = value && typeof value === 'object' ? value : emptyNameValue();
          data[nameFieldId.value] = {
            first_name: (obj.first_name || '').trim(),
            last_name: (obj.last_name || '').trim(),
            full_name: [obj.first_name, obj.last_name].filter(Boolean).join(' ').trim(),
          };
          return;
        }
        data[nameFieldId.value] = typeof value === 'string' ? value.trim() : '';
      }

      const nameFieldValue = computed(() => {
        const data = formDataRef?.value;
        if (data && typeof data === 'object') {
          return readNameFromFormData(data);
        }
        if (nameUsesSplitFields.value) {
          return typeof localNameValue.value === 'object'
            ? localNameValue.value
            : emptyNameValue();
        }
        return typeof localNameValue.value === 'string' ? localNameValue.value : '';
      });

      const emailFieldValue = computed(() => {
        const data = formDataRef?.value;
        if (data && typeof data[emailFieldId.value] === 'string') {
          return data[emailFieldId.value];
        }
        return localEmailValue.value;
      });

      function onNameFieldUpdate(value) {
        if (formDataRef?.value) {
          writeNameToFormData(formDataRef.value, value);
        } else if (nameUsesSplitFields.value) {
          localNameValue.value =
            value && typeof value === 'object' ? { ...value } : emptyNameValue();
        } else {
          localNameValue.value = typeof value === 'string' ? value : '';
        }
      }

      function onEmailFieldUpdate(value) {
        const email = typeof value === 'string' ? value : '';
        if (formDataRef?.value) {
          formDataRef.value[emailFieldId.value] = email;
        } else {
          localEmailValue.value = email;
        }
      }

      function isNameFilled(value) {
        if (nameUsesSplitFields.value) {
          if (!value || typeof value !== 'object') return false;
          return (
            (value.first_name || '').trim().length > 0 &&
            (value.last_name || '').trim().length > 0
          );
        }
        return typeof value === 'string' && value.trim().length > 0;
      }

      const canSubmitBooking = computed(() => {
        const email = emailFieldValue.value.trim();
        return isNameFilled(nameFieldValue.value) && isValidEmail(email);
      });

      function prefillAttendeeFromFormData() {
        const data = formDataRef?.value;
        if (!data || typeof data !== 'object') return;

        const nameFromForm = readNameFromFormData(data);
        if (nameUsesSplitFields.value) {
          if (nameFromForm && typeof nameFromForm === 'object') {
            localNameValue.value = { ...nameFromForm };
          }
        } else if (typeof nameFromForm === 'string' && nameFromForm) {
          localNameValue.value = nameFromForm;
        }

        if (typeof data[emailFieldId.value] === 'string' && data[emailFieldId.value].trim()) {
          localEmailValue.value = data[emailFieldId.value].trim();
        }
      }

      function syncAttendeeToFormData() {
        const data = formDataRef?.value;
        if (!data || typeof data !== 'object') return;
        writeNameToFormData(data, nameFieldValue.value);
        data[emailFieldId.value] = emailFieldValue.value.trim();
      }

      // Computed: has locations
      const hasLocations = computed(() => locations.value.length > 0);

      // Computed: selected location name
      const selectedLocationName = computed(() => {
        if (!selectedLocationId.value) return null;
        const location = locations.value.find((l) => l.id === selectedLocationId.value);
        return location?.name || null;
      });

      // Computed: selected location type
      const selectedLocationType = computed(() => {
        if (!selectedLocationId.value) return null;
        const location = locations.value.find((l) => l.id === selectedLocationId.value);
        return location?.type || null;
      });

      // Group locations by the type the visitor picks first. Anything that is not
      // online/custom is a fixed location.
      const LOCATION_KIND_ICONS = { online: 'video', custom: 'home', fixed: 'building' };
      const kindOfLocation = (location) =>
        location?.type === 'online' || location?.type === 'custom' ? location.type : 'fixed';

      const locationsByKind = computed(() => {
        const groups = { online: [], custom: [], fixed: [] };
        for (const location of locations.value) {
          groups[kindOfLocation(location)].push(location);
        }
        return groups;
      });

      const availableLocationKinds = computed(() =>
        ['online', 'custom', 'fixed'].filter((kind) => locationsByKind.value[kind].length > 0)
      );

      const selectedLocationKind = ref('');
      const kindLocations = computed(() => locationsByKind.value[selectedLocationKind.value] || []);

      const showFixedLocationList = computed(
        () => selectedLocationKind.value === 'fixed' && locationsByKind.value.fixed.length > 1
      );
      // Several locations of one non-fixed type: keep the old select so all stay reachable
      const showKindSelect = computed(
        () => selectedLocationKind.value !== 'fixed' && kindLocations.value.length > 1
      );

      watch(
        availableLocationKinds,
        (kinds) => {
          if (kinds.length === 1 && !selectedLocationKind.value) {
            selectedLocationKind.value = kinds[0];
          }
        },
        { immediate: true }
      );

      // Keep the type in sync when the location is set from outside (auto-select).
      watch(selectedLocationId, (id) => {
        const location = locations.value.find((l) => l.id === id);
        if (location) selectedLocationKind.value = kindOfLocation(location);
      });

      const formatLocationAddress = (location) => {
        const address = location?.address;
        if (!address) return '';
        if (address.formatted) return address.formatted;
        const street = [address.street, address.number].filter(Boolean).join(' ');
        const city = [address.postalCode, address.city].filter(Boolean).join(' ');
        return [street, city].filter(Boolean).join(', ');
      };

      // Weekdays from translations
      const weekdaysShort = computed(() => {
        const localeKey = locale.value;
        const allMessages = messages.value || {};
        const localeMessages = allMessages[localeKey] || {};
        return localeMessages.form?.fields?.scheduling?.weekdays_short || [];
      });

      // ============================================
      // LIFECYCLE
      // ============================================
        onMounted(async () => {
        schedulingService.value = await getSchedulingService(props.dummy);
        prefillAttendeeFromFormData();
        await fetchLocations();
        await fetchAvailableSlots();
        if (typeof registerFieldValidator === 'function') {
          registerFieldValidator(props.fieldId, validateForPage);
        }
      });

      onMounted(() => {
        updateCalendarHeight();
        window.addEventListener('resize', handleResize);
      });

      onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
        if (typeof unregisterFieldValidator === 'function') {
          unregisterFieldValidator(props.fieldId);
        }
        if (typeof setSubmitTakeover === 'function') {
          setSubmitTakeover(props.fieldId, false);
        }
      });

      watch([selectedMonth, selectedDay], async () => {
        await nextTick();
        updateCalendarHeight();
      });

      // ============================================
      // LOCATION HANDLING
      // ============================================
      const fetchLocations = async () => {
        if (!schedulingService.value?.getLocations) {
          return;
        }

        try {
          const response = await schedulingService.value.getLocations(props.formId, props.fieldId);

          locations.value = response.locations || [];
          requireLocation.value = response.requireLocation || false;
          isRoundRobin.value = response.isRoundRobin || false;

          // Auto-select first location if only one exists
          if (locations.value.length === 1) {
            selectedLocationId.value = locations.value[0].id;
          }
        } catch (err) {
          // Don't show error for locations, just continue without them
          console.warn('[SchedulingElement] Could not fetch locations:', err);
        }
      };

      // Inline address validation error (shown under AddressElement)
      const addressError = ref('');

      const isValidDutchPostalCode = (postalCode) =>
        /^\d{4}\s?[A-Za-z]{2}$/.test((postalCode || '').trim());

      /**
       * Compact/smart address: user only fills postal code + house number,
       * street/city come from lookup. Validate visible fields first.
       */
      const getCustomAddressValidationError = (addr) => {
        if (!addr || typeof addr !== 'object') {
          return (
            t('form.fields.scheduling.enter_address_first') || 'Voer eerst je adres in'
          );
        }

        const street = (addr.street || '').trim();
        const houseNumber = (addr.houseNumber || '').trim();
        const postalCode = (addr.postalCode || '').trim();
        const city = (addr.city || '').trim();
        const country = (addr.country || 'NL').toUpperCase();
        const isNl = country === 'NL' || country === 'NEDERLAND' || country === '';

        const hasAnyValue = !!(street || houseNumber || postalCode || city);
        if (!hasAnyValue) {
          return (
            t('form.fields.scheduling.enter_address_first') || 'Voer eerst je adres in'
          );
        }

        if (postalCode && isNl && !isValidDutchPostalCode(postalCode)) {
          return (
            t('form.fields.scheduling.address_postal_code_invalid') ||
            t('form.fields.address.postal_code_invalid') ||
            'Vul een geldige postcode in (bijv. 1234 AB)'
          );
        }

        if (!postalCode) {
          return (
            t('form.fields.scheduling.address_postal_code_required') ||
            t('form.error.address_postal_code_required') ||
            'Postcode is verplicht'
          );
        }
        if (!houseNumber) {
          return (
            t('form.fields.scheduling.address_house_number_required') ||
            t('form.error.address_house_number_required') ||
            'Huisnummer is verplicht'
          );
        }

        if (!street || !city) {
          return (
            t('form.fields.address.not_found') ||
            'Adres niet gevonden. Controleer postcode en huisnummer.'
          );
        }

        return '';
      };

      const isCustomAddressValid = computed(() => {
        if (selectedLocationType.value !== 'custom') return true;
        return !getCustomAddressValidationError(customAddress.value);
      });

      const coveragePostalCode = computed(() => {
        if (selectedLocationType.value !== 'custom' || !isCustomAddressValid.value) return null;
        return (customAddress.value.postalCode || '').trim() || null;
      });

      const addressConfirmed = ref(false);

      const visitorLocationPayload = (addr) => {
        if (!addr) return null;
        const street = (addr.street || '').trim();
        const houseNumber = (addr.houseNumber || '').trim();
        const postalCode = (addr.postalCode || '').trim();
        const city = (addr.city || '').trim();
        if (!street || !houseNumber || !postalCode || !city) return null;
        const coords = addr.coordinates;
        const coordinates =
          Number.isFinite(coords?.lat) && Number.isFinite(coords?.lng)
            ? { lat: coords.lat, lng: coords.lng }
            : null;
        return {
          street,
          number: houseNumber,
          postalCode,
          city,
          country: (addr.country || 'NL').trim() || 'NL',
          formatted: [
            [street, houseNumber].filter(Boolean).join(' '),
            [postalCode, city].filter(Boolean).join(' '),
          ]
            .filter(Boolean)
            .join(', '),
          ...(addr.placeId ? { placeId: addr.placeId } : {}),
          ...(coordinates ? { coordinates } : {}),
        };
      };

      const confirmedCustomLocation = computed(() => {
        if (selectedLocationType.value !== 'custom' || !addressConfirmed.value) return null;
        return visitorLocationPayload(customAddress.value);
      });

      const slotsLocked = computed(
        () =>
          selectedLocationType.value === 'custom' &&
          (!addressConfirmed.value || !isCustomAddressValid.value || noCoverage.value)
      );

      const validateForPage = () => {
        if (requireLocation.value && hasLocations.value && !selectedLocationId.value) {
          return (
            t('form.fields.scheduling.choose_location_first') || 'Kies eerst een locatie'
          );
        }

        if (selectedLocationType.value === 'custom') {
          const validationError = getCustomAddressValidationError(customAddress.value);
          addressError.value = validationError;
          if (!validationError && typeof clearFieldError === 'function') {
            clearFieldError(props.fieldId);
          }
          if (validationError) return validationError;
        }

        if (showBookingConfirm.value && !hasConfirmedBooking.value) {
          return t('form.fields.scheduling.booking_finish_details');
        }

        return '';
      };

      const handleLocationChange = async () => {
        // Reset calendar selection and custom address
        selectedDay.value = null;
        selectedSlot.value = null;
        addressError.value = '';
        error.value = null;
        customAddress.value = {
          houseNumber: '',
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        };
        noCoverage.value = false;
        showFullCalendar.value = false;
        resetBookingConfirm();
        emit('update:modelValue', null);
        // Always fetch slots when location changes (including custom)
        await fetchAvailableSlots();
      };

      const selectLocationKind = async (kind) => {
        if (selectedLocationKind.value === kind) return;
        selectedLocationKind.value = kind;
        const options = locationsByKind.value[kind];
        // A single location of this type is picked right away; otherwise the visitor
        // chooses in the list/select that appears below.
        selectedLocationId.value = options.length === 1 ? options[0].id : '';
        await handleLocationChange();
      };

      const handleAddressViewState = (state) => {
        const confirmed = state === 'confirmed';
        const wasConfirmed = addressConfirmed.value;
        addressConfirmed.value = confirmed;
        if (wasConfirmed && !confirmed) {
          selectedSlot.value = null;
          resetBookingConfirm();
          emit('update:modelValue', null);
          addressError.value = t('form.fields.scheduling.confirm_address');
          return;
        }
        if (confirmed) {
          const message = t('form.fields.scheduling.confirm_address');
          if (addressError.value === message) addressError.value = '';
          if (error.value === message) error.value = null;
        }
      };

      const handleCustomAddressUpdate = (newAddress) => {
        customAddress.value = newAddress;

        const validationError = getCustomAddressValidationError(newAddress);
        if (addressError.value || !validationError) {
          addressError.value = validationError;
        }
        if (!validationError && typeof clearFieldError === 'function') {
          clearFieldError(props.fieldId);
        }
        if (error.value && isCustomAddressValid.value) {
          error.value = null;
        }
      };

      // ============================================
      // SLOTS HANDLING
      // ============================================
      /** Remove a slot from local state so it no longer appears (e.g. after 404 from reserve). */
      function removeSlotFromCalendar(slotId) {
        if (!slotId || !months.value?.length) return;
        const id = String(slotId);
        months.value = months.value.map((month) => ({
          ...month,
          days: month.days.map((day) => {
            const slots = (day.slots || []).filter((s) => String(s?.id) !== id);
            if (slots.length === (day.slots || []).length) return day;
            return { ...day, slots, slotsCount: slots.length };
          }),
        }));
        if (selectedDay.value?.slots?.some((s) => String(s?.id) === id)) {
          const day = selectedDay.value;
          const slots = (day.slots || []).filter((s) => String(s?.id) !== id);
          selectedDay.value = { ...day, slots, slotsCount: slots.length };
          timeSlots.value = slots.map((slot) => ({
            id: slot.id,
            time: slot.time,
            startTime: slot.startTime,
            endTime: slot.endTime,
            status: slot.status,
            integrationCount: slot.integrationCount,
          }));
        }
      }

      // One key for postcode and coordinates. Two watchers each started a fetch,
      // and each fetch unmounted the calendar, so it flashed twice.
      const slotQueryKey = computed(() => {
        const location = confirmedCustomLocation.value;
        const coords = location?.coordinates;
        const coordKey = coords ? `${coords.lat},${coords.lng}` : '';
        return `${coveragePostalCode.value || ''}|${coordKey}|${location?.formatted || ''}`;
      });

      let slotsRequestId = 0;

      const logTravelDebug = (travelDebug) => {
        if (!Array.isArray(travelDebug)) return;
        const formatWhen = (iso) =>
          new Date(iso).toLocaleString('nl-NL', {
            timeZone: 'Europe/Amsterdam',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          });
        const mapsUrl = (from, to) =>
          `https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving`;

        console.group('[reistijd] Afstanden om te vergelijken met Google Maps');
        if (travelDebug.length === 0) {
          console.log(
            'Geen rit berekend. Er is geen buurafspraak met een adres, of reistijd staat uit voor dit adres.'
          );
          console.groupEnd();
          return;
        }
        const rows = travelDebug.map((check) => ({
          tijd: formatWhen(check.slotStart),
          richting:
            check.direction === 'toVisitor' ? 'naar dit adres' : 'naar de volgende afspraak',
          van: check.fromLabel,
          naar: check.toLabel,
          km: check.km,
          minuten: check.minutes,
          gat: check.gapMinutes,
          buffer: check.bufferMinutes,
          nodig: check.requiredMinutes,
          past: check.fits ? 'ja' : 'nee',
          bron: check.fallback ? 'hemelsbreed' : 'Google',
          maps: mapsUrl(check.from, check.to),
        }));
        console.table(rows);
        rows.forEach((row) => {
          const label = row.past === 'ja' ? 'past' : 'valt af';
          console.log(`${label} ${row.tijd} (${row.richting})`, row.maps);
        });
        console.groupEnd();
      };

      const ensureSchedulingVisitorId = () => {
        if (typeof sessionStorage === 'undefined') return null;
        const visitorKey = `scheduling-visitor:${props.formId}`;
        const stored = sessionStorage.getItem(visitorKey);
        if (stored && /^[A-Za-z0-9-]{8,80}$/.test(stored)) return stored;
        const created =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
        sessionStorage.setItem(visitorKey, created);
        return created;
      };

      const clearLoadedSlots = () => {
        months.value = [];
        suggestions.value = null;
        selectedDay.value = null;
        timeSlots.value = [];
      };

      const canFetchAvailableSlots = () => {
        if (hasLocations.value && !selectedLocationId.value) {
          return false;
        }
        if (selectedLocationType.value === 'custom') {
          if (!addressConfirmed.value || !isCustomAddressValid.value) {
            return false;
          }
        }
        return true;
      };

      const fetchAvailableSlots = async (yearMonth = null) => {
        if (!schedulingService.value) return;

        if (!canFetchAvailableSlots()) {
          clearLoadedSlots();
          loading.value = false;
          return;
        }

        const requestId = ++slotsRequestId;
        const showSpinner = months.value.length === 0;
        if (showSpinner) {
          loading.value = true;
        }
        error.value = null;
        const currentLang = locale?.value || 'en-EN';

        if (!yearMonth) {
          const now = new Date();
          yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        }

        try {
          const response = await schedulingService.value.getAvailableSlots(
            props.formId,
            props.fieldId,
            currentLang,
            selectedLocationId.value || null,
            yearMonth,
            coveragePostalCode.value,
            confirmedCustomLocation.value,
            ensureSchedulingVisitorId()
          );

          if (requestId !== slotsRequestId) return;

          currentYearMonth.value = yearMonth;
          months.value = response.months || [];
          suggestions.value = response.suggestions || null;
          isRoundRobin.value = response.isRoundRobin || false;
          noCoverage.value = response.noCoverage === true;
          logTravelDebug(response.travelDebug);

          if (months.value.length > 0) {
            selectedMonth.value = months.value[0].name;
          } else if (response.currentMonth) {
            selectedMonth.value = response.currentMonth;
          }

          selectedDay.value = null;
          timeSlots.value = [];
        } catch (err) {
          if (requestId !== slotsRequestId) return;
          error.value = t('form.fields.scheduling.error') || 'Failed to load available slots';
          console.error(err);
        } finally {
          if (requestId === slotsRequestId) {
            loading.value = false;
          }
        }
      };

      watch(slotQueryKey, (next, previous) => {
        if (next === previous) return;
        if (!canFetchAvailableSlots()) {
          clearLoadedSlots();
          return;
        }
        showFullCalendar.value = false;
        // Drop the previous list so a day cannot show times from before this address.
        months.value = [];
        suggestions.value = null;
        selectedDay.value = null;
        selectedSlot.value = null;
        fetchAvailableSlots(currentYearMonth.value);
      });

      // ============================================
      // CALENDAR HELPERS
      // ============================================
      function updateCalendarHeight() {
        if (window.innerWidth <= 768) {
          calendarHeight.value = null;
          return;
        }
        if (calendarRef.value) {
          calendarHeight.value = calendarRef.value.offsetHeight;
        }
      }

      function handleResize() {
        updateCalendarHeight();
      }

      function formatClock(date) {
        return date.toLocaleTimeString('nl-NL', {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        });
      }

      function formatToTimeZone(date, onlyStart = false) {
        if (!date?.startTime) return '';
        const start = new Date(date.startTime);
        const startTimeStr = formatClock(start);

        if (onlyStart || !date?.endTime) {
          return startTimeStr;
        }

        const end = new Date(date.endTime);
        const endTimeStr = formatClock(end);

        return `${startTimeStr} - ${endTimeStr}`;
      }

      // ============================================
      // SELECTION HANDLERS
      // ============================================
      const selectDay = async (day) => {
        if (day.slotsCount > 0) {
          selectedDay.value = day;
          timeSlots.value = day.slots.map((slot) => ({
            id: slot.id,
            time: slot.time,
            startTime: slot.startTime,
            endTime: slot.endTime,
            status: slot.status,
            integrationCount: slot.integrationCount,
          }));
          selectedSlot.value = null;
          resetBookingConfirm();
          emit('update:modelValue', null);
        }
      };

      function findDayForSlot(slotId) {
        for (const month of months.value || []) {
          for (const day of month.days || []) {
            if ((day.slots || []).some((slot) => slot.id === slotId)) return day;
          }
        }
        return null;
      }

      const selectSuggestion = async (pin) => {
        if (!pin?.slotId || slotsLocked.value) return;
        const day = findDayForSlot(pin.slotId);
        if (!day) return;
        await selectDay(day);
        const slot = (day.slots || []).find((item) => item.id === pin.slotId);
        if (!slot) return;
        await pickTimeSlot(slot);
      };

      function resetBookingConfirm() {
        bookingStep.value = null;
        pendingSlot.value = null;
        bookingDetailsError.value = '';
      }

      function backFromBookingConfirm() {
        resetBookingConfirm();
        selectedSlot.value = null;
      }

      const pickTimeSlot = async (slot) => {
        if (slot.status !== 'available') return;
        if (slotsLocked.value) {
          if (selectedLocationType.value === 'custom' && !addressConfirmed.value) {
            const message = t('form.fields.scheduling.confirm_address');
            addressError.value = message;
            error.value = message;
          } else if (selectedLocationType.value === 'custom' && !isCustomAddressValid.value) {
            const validationError = getCustomAddressValidationError(customAddress.value);
            addressError.value = validationError;
            error.value = validationError;
          } else if (noCoverage.value) {
            error.value = t('form.fields.scheduling.out_of_service_area');
          }
          return;
        }

        if (selectedLocationType.value === 'custom') {
          const validationError = getCustomAddressValidationError(customAddress.value);
          if (validationError) {
            addressError.value = validationError;
            error.value = validationError;
            return;
          }
        }

        prefillAttendeeFromFormData();
        selectedSlot.value = slot;
        pendingSlot.value = slot;
        bookingStep.value = 'confirm';
        bookingDetailsError.value = '';
        addressError.value = '';
        error.value = null;
      };

      const selectTimeSlot = (slot) => pickTimeSlot(slot);

      const confirmBooking = async () => {
        // Slot already reserved (an earlier submit failed on another field): only
        // finish the form again, don't reserve twice.
        if (hasConfirmedBooking.value) {
          if (typeof requestFormAdvance === 'function') await requestFormAdvance();
          return;
        }

        const slot = pendingSlot.value;
        if (!slot || isReserving.value) return;

        bookingDetailsError.value = '';
        if (!canSubmitBooking.value) {
          if (!isNameFilled(nameFieldValue.value)) {
            bookingDetailsError.value = t('form.fields.scheduling.booking_name_required');
          } else {
            bookingDetailsError.value = t('form.error.email_invalid');
          }
          return;
        }

        syncAttendeeToFormData();

        let reserved = false;
        try {
          isReserving.value = true;

          const visitorId = ensureSchedulingVisitorId();
          const draftKey = `scheduling-draft:${props.formId}`;
          let subId = props.dummy
            ? 'builder-preview'
            : props.submissionId ||
              sessionService.currentSubmissionId?.value ||
              (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(draftKey) : null);
          if (!subId) {
            // A failing draft is not a taken slot: report it as a generic reserve error
            try {
              const response = await sessionService.createDraftSubmission(props.formId);
              subId = response?.submissionId;
            } catch (draftError) {
              console.error(draftError);
            }
            if (!subId) {
              bookingDetailsError.value = t('form.fields.scheduling.reserve_error');
              return;
            }
          }
          if (!props.dummy && typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(draftKey, subId);
          }
          if (!props.dummy && sessionService.currentSubmissionId) {
            sessionService.currentSubmissionId.value = subId;
          }

          const customLocationData =
            selectedLocationType.value === 'custom'
              ? visitorLocationPayload(customAddress.value)
              : null;

          const reservation = await schedulingService.value.reserveSlot(
            props.formId,
            slot.id,
            subId,
            selectedLocationId.value || null,
            customLocationData,
            visitorId
          );

          const actualSlotId = reservation?.slot?.slot_id || slot.id;
          emit('update:modelValue', actualSlotId);
          if (typeof clearFieldError === 'function') {
            clearFieldError(props.fieldId);
          }
          reserved = true;
        } catch (err) {
          const status = err?.response?.status;
          const errorCode = err?.response?.data?.error_code;
          const apiError = err?.response?.data?.error;

          if (status === 400 || errorCode === 'INVALID_CUSTOM_LOCATION') {
            const message =
              apiError ||
              t('form.fields.scheduling.enter_address_first') ||
              'Voer eerst je adres in';
            addressError.value = message;
            error.value = message;
            resetBookingConfirm();
            selectedSlot.value = null;
            emit('update:modelValue', null);
            return;
          }

          if (errorCode === 'TRAVEL_INFEASIBLE') {
            const heldDay = selectedDay.value?.date || null;
            const message =
              t('form.fields.scheduling.slot_not_available') ||
              'Dit tijdslot is niet meer beschikbaar, kies een ander tijdstip';
            removeSlotFromCalendar(slot.id);
            await fetchAvailableSlots();
            error.value = message;
            if (heldDay) {
              const day = (months.value[0]?.days || []).find((item) => item.date === heldDay);
              if (day?.slotsCount > 0) selectedDay.value = day;
            }
            resetBookingConfirm();
            selectedSlot.value = null;
            emit('update:modelValue', null);
            return;
          }

          if (errorCode === 'OUT_OF_SERVICE_AREA') {
            noCoverage.value = true;
            error.value = t('form.fields.scheduling.out_of_service_area');
            resetBookingConfirm();
            selectedSlot.value = null;
            emit('update:modelValue', null);
            return;
          }

          if (status === 409 || status === 404 || errorCode === 'SLOT_NOT_AVAILABLE') {
            const heldDay = selectedDay.value?.date || null;
            const message =
              t('form.fields.scheduling.slot_not_available') ||
              'Dit tijdslot is niet meer beschikbaar, kies een ander tijdstip';
            removeSlotFromCalendar(slot.id);
            await fetchAvailableSlots();
            error.value = message;
            if (heldDay) {
              const day = (months.value[0]?.days || []).find((item) => item.date === heldDay);
              if (day?.slotsCount > 0) selectedDay.value = day;
            }
            resetBookingConfirm();
            selectedSlot.value = null;
            emit('update:modelValue', null);
            return;
          }

          bookingDetailsError.value = t('form.fields.scheduling.reserve_error');
          console.error(err);
        } finally {
          isReserving.value = false;
        }

        // formData must hold the slot before the form validates and submits
        if (reserved && typeof requestFormAdvance === 'function') {
          await nextTick();
          await requestFormAdvance();
        }
      };

      const getCurrentMonthDays = computed(() => {
        if (!selectedMonth.value || !months.value.length) return [];
        const month = months.value.find((m) => m.name === selectedMonth.value);
        return month ? month.days : [];
      });

      const calendarCells = computed(() => {
        const days = getCurrentMonthDays.value;
        if (!days.length) return [];
        const firstDay = days[0];
        const firstDate = dayjs(firstDay.date);
        let startIdx = firstDate.day() - 1;
        if (startIdx < 0) startIdx = 6;
        const cells = [];
        for (let i = 0; i < startIdx; i++) {
          cells.push(null);
        }
        for (const day of days) {
          cells.push(day);
        }
        while (cells.length % 7 !== 0) {
          cells.push(null);
        }
        return cells;
      });

      dayjs.extend(localizedFormat);
      const formattedSelectedDate = computed(() => {
        if (!selectedDay.value?.date) return '';
        const localeShort = (locale.value || 'en').split('-')[0];
        return dayjs(selectedDay.value.date).locale(localeShort).format('dddd D MMMM YYYY');
      });

      function formatSuggestionPinLabels(startTime) {
        if (!startTime) {
          return { dateLabel: '', timeLabel: '', monthLabel: '', dayLabel: '', titleLabel: '', clockLabel: '' };
        }
        const localeShort = (locale.value || 'en').split('-')[0];
        const date = dayjs(startTime).locale(localeShort);
        const dateLabel = date.format('dddd D MMMM YYYY');
        const clockLabel = formatClock(new Date(startTime));
        const timeLabel = localeShort === 'nl' ? `${clockLabel} uur` : clockLabel;
        return {
          dateLabel,
          timeLabel,
          monthLabel: date.format('MMM'),
          dayLabel: date.format('D'),
          titleLabel: date.format('dddd D MMMM'),
          clockLabel,
        };
      }

      const PIN_KIND_LABEL_KEYS = {
        route: 'suggestion_route',
        morning: 'suggestion_morning',
        afternoon: 'suggestion_afternoon',
        evening: 'suggestion_evening',
        earliest: 'suggestion_earliest',
        leastTravel: 'suggestion_least_travel',
      };
      const pinKindLabel = (kind) => {
        const key = PIN_KIND_LABEL_KEYS[kind];
        return key ? t(`form.fields.scheduling.${key}`) : '';
      };

      const toPin = (source, kind) => ({
        kind,
        slotId: source.slotId,
        startTime: source.startTime,
        ...formatSuggestionPinLabels(source.startTime),
        kindLabel: pinKindLabel(kind),
      });

      const suggestionPins = computed(() => {
        const source = suggestions.value || {};
        // The API sends up to three picks (route fit, morning, afternoon, evening)
        if (Array.isArray(source.list) && source.list.length > 0) {
          return source.list.filter((pin) => pin?.slotId).map((pin) => toPin(pin, pin.kind));
        }
        const pins = [];
        if (source.earliest?.slotId) pins.push(toPin(source.earliest, 'earliest'));
        if (source.leastTravel?.slotId && source.leastTravel.slotId !== source.earliest?.slotId) {
          pins.push(toPin(source.leastTravel, 'leastTravel'));
        }
        return pins;
      });

      // Shown in the appointment step subtitle: the visitor's address or the chosen location
      const locationHint = computed(() => {
        if (selectedLocationType.value === 'custom') {
          const street = (customAddress.value.street || '').trim();
          const postalCode = (customAddress.value.postalCode || '').trim();
          return [street, postalCode].filter(Boolean).join(', ');
        }
        return selectedLocationName.value || '';
      });

      const awaitingCustomAddress = computed(() => {
        if (selectedLocationType.value !== 'custom') return false;
        return !addressConfirmed.value || !isCustomAddressValid.value;
      });

      // Flow: 1) location (+ address) -> 2) appointment -> 3) confirm (bookingStep).
      // A single non-custom location needs no choice, so step 1 is skipped.
      const flowStep = ref('location');

      const locationStepNeeded = computed(
        () =>
          hasLocations.value &&
          !(locations.value.length === 1 && locations.value[0].type !== 'custom')
      );

      const showLocationStep = computed(
        () => locationStepNeeded.value && flowStep.value === 'location' && !showBookingConfirm.value
      );

      const showAppointmentStep = computed(
        () => !locationStepNeeded.value || flowStep.value === 'appointment'
      );

      const showBackToLocation = computed(
        () => locationStepNeeded.value && flowStep.value === 'appointment' && !showBookingConfirm.value
      );

      const goToAppointmentStep = () => {
        if (!selectedLocationId.value) return;
        if (selectedLocationType.value === 'custom') {
          if (!addressConfirmed.value) {
            addressError.value = t('form.fields.scheduling.confirm_address');
            return;
          }
          const validationError = getCustomAddressValidationError(customAddress.value);
          if (validationError) {
            addressError.value = validationError;
            return;
          }
        }
        addressError.value = '';
        flowStep.value = 'appointment';
      };

      const backToLocationStep = () => {
        closeFullCalendar();
        flowStep.value = 'location';
      };

      // Header shared by all steps: label · duration, title, optional extras
      const stepTitle = computed(() => {
        if (showBookingConfirm.value) return t('form.fields.scheduling.booking_your_information');
        if (showLocationStep.value) return t('form.fields.scheduling.select_location');
        return t('form.fields.scheduling.choose_appointment_title');
      });

      const stepSubtitle = computed(() => {
        if (showBookingConfirm.value) return '';
        if (showLocationStep.value) return props.caption || '';
        if (showPinCards.value) {
          return locationHint.value
            ? t('form.fields.scheduling.choose_appointment_subtitle_near')
            : t('form.fields.scheduling.choose_appointment_subtitle');
        }
        return '';
      });

      const stepSubtitleHighlight = computed(() =>
        !showBookingConfirm.value && !showLocationStep.value && showPinCards.value
          ? locationHint.value
          : ''
      );

      const chipDateTimeLabel = computed(() => {
        const slot = pendingSlot.value || selectedSlot.value;
        if (!slot?.startTime) return '';
        const localeShort = (locale.value || 'en').split('-')[0];
        const label = dayjs(slot.startTime).locale(localeShort).format('ddd D MMMM, HH:mm');
        return label.charAt(0).toUpperCase() + label.slice(1);
      });

      const headerChips = computed(() => {
        // Only the last step summarises the choices made in the earlier steps
        if (!showBookingConfirm.value) return [];
        const chips = [];
        if (chipDateTimeLabel.value) {
          chips.push({ icon: 'calendar-clock', text: chipDateTimeLabel.value });
        }
        if (selectedLocationName.value) {
          chips.push({ icon: 'location', text: selectedLocationName.value });
        }
        return chips;
      });

      const showNoCoveragePin = computed(() => {
        if (selectedLocationType.value !== 'custom') return false;
        if (!addressConfirmed.value || !isCustomAddressValid.value) return false;
        return noCoverage.value;
      });

      const canShowBookingUi = computed(() => {
        if (awaitingCustomAddress.value) return false;
        return months.value.length > 0 || showNoCoveragePin.value;
      });

      const showPinCards = computed(
        () => !showNoCoveragePin.value && suggestionPins.value.length > 0
      );

      const showCalendarPicker = computed(() => {
        if (showNoCoveragePin.value) return false;
        if (suggestionPins.value.length === 0) return true;
        return showFullCalendar.value;
      });

      const openFullCalendar = () => {
        showFullCalendar.value = true;
        selectedDay.value = null;
        selectedSlot.value = null;
        resetBookingConfirm();
        emit('update:modelValue', null);
      };

      const closeFullCalendar = () => {
        showFullCalendar.value = false;
        selectedDay.value = null;
        selectedSlot.value = null;
        resetBookingConfirm();
        emit('update:modelValue', null);
      };

      const bookSuggestedSlot = (pin) => selectSuggestion(pin);

      const scrollAmount = computed(() => {
        return daysContainer.value?.offsetWidth || 0;
      });

      // ============================================
      // MONTH NAVIGATION
      // ============================================
      const canGoBack = computed(() => {
        if (!currentYearMonth.value) return false;
        const now = new Date();
        const minMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return currentYearMonth.value > minMonth;
      });

      const goToNextMonth = () => {
        if (!currentYearMonth.value) return;
        const [year, month] = currentYearMonth.value.split('-').map(Number);
        const next =
          month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
        fetchAvailableSlots(next);
      };

      const goToPreviousMonth = () => {
        if (!canGoBack.value) return;
        const [year, month] = currentYearMonth.value.split('-').map(Number);
        const prev =
          month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
        fetchAvailableSlots(prev);
      };

      const getAmsterdamCalendarDateIso = () =>
        new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());

      const todayIsoDate = getAmsterdamCalendarDateIso();

      const findDayByIsoDate = (isoDate) => {
        for (const month of months.value || []) {
          const day = (month.days || []).find((item) => item.date === isoDate);
          if (day) return day;
        }
        return null;
      };

      const goToToday = async () => {
        const todayIso = getAmsterdamCalendarDateIso();
        const todayMonth = todayIso.slice(0, 7);

        if (currentYearMonth.value !== todayMonth) {
          await fetchAvailableSlots(todayMonth);
        }

        const day = findDayByIsoDate(todayIso);
        if (day?.slotsCount > 0) {
          await selectDay(day);
          return;
        }

        selectedDay.value = null;
        selectedSlot.value = null;
        resetBookingConfirm();
        emit('update:modelValue', null);
      };

      // ============================================
      // NAVIGATION (legacy scroll)
      // ============================================
      const nextDays = () => {
        if (daysContainer.value) {
          daysContainer.value.scrollBy({
            left: scrollAmount.value,
            behavior: 'smooth',
          });
        }
      };

      const previousDays = () => {
        if (daysContainer.value) {
          daysContainer.value.scrollBy({
            left: -scrollAmount.value,
            behavior: 'smooth',
          });
        }
      };

      watch(selectedMonth, () => {
        if (daysContainer.value) {
          daysContainer.value.scrollLeft = 0;
        }
      });

      // ============================================
      // RETURN
      // ============================================
      return {
        // State
        loading,
        isReserving,
        error,
        // Locations
        locations,
        selectedLocationId,
        selectedLocationName,
        selectedLocationType,
        requireLocation,
        hasLocations,
        handleLocationChange,
        LOCATION_KIND_ICONS,
        locationsByKind,
        availableLocationKinds,
        selectedLocationKind,
        kindLocations,
        showFixedLocationList,
        showKindSelect,
        formatLocationAddress,
        selectLocationKind,
        customAddress,
        handleCustomAddressUpdate,
        handleAddressViewState,
        isCustomAddressValid,
        addressError,
        noCoverage,
        slotsLocked,
        // Calendar
        months,
        selectedMonth,
        currentYearMonth,
        selectedDay,
        timeSlots,
        selectedSlot,
        getCurrentMonthDays,
        calendarCells,
        todayIsoDate,
        calendarHeight,
        calendarRef,
        formattedSelectedDate,
        suggestionPins,
        awaitingCustomAddress,
        locationStepNeeded,
        showLocationStep,
        showAppointmentStep,
        showBackToLocation,
        locationHint,
        stepTitle,
        stepSubtitle,
        stepSubtitleHighlight,
        headerChips,
        goToAppointmentStep,
        backToLocationStep,
        showNoCoveragePin,
        canShowBookingUi,
        showPinCards,
        showCalendarPicker,
        showFullCalendar,
        openFullCalendar,
        closeFullCalendar,
        bookSuggestedSlot,
        showBookingConfirm,
        hasConfirmedBooking,
        takesOverSubmit,
        formIsSubmitting,
        pendingSlot,
        nameFieldProps,
        emailFieldProps,
        nameFieldValue,
        emailFieldValue,
        onNameFieldUpdate,
        onEmailFieldUpdate,
        bookingDetailsError,
        confirmDateTimeLabel,
        slotLengthMinutes,
        confirmBooking,
        backFromBookingConfirm,
        // Month navigation
        canGoBack,
        goToNextMonth,
        goToPreviousMonth,
        goToToday,
        // Methods
        selectDay,
        selectSuggestion,
        formatToTimeZone,
        selectTimeSlot,
        daysContainer,
        nextDays,
        previousDays,
        // i18n
        t,
        weekdaysShort,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@shared/assets/css/base/mixins.scss';
  .scheduling {
    border: 1px solid var(--cs-input-default-border);
    border-radius: 0.5rem;
    background-color: var(--cs-input-default-background);
    color: var(--cs-input-default-text);

    &__header {
      padding: 1.25rem 1.25rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__header-top {
      font-size: 0.875rem;
      color: var(--cs-text-body);
      opacity: 0.7;
    }

    &__header-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1.2;
      color: var(--cs-text-heading);
    }

    &__header-subtitle {
      margin: 0;
      font-size: 0.9375rem;
      color: var(--cs-text-body);
      opacity: 0.8;

      strong {
        color: var(--cs-text-heading);
      }
    }

    &__header-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem 1.25rem;
      margin: 0.25rem 0 0;
      padding: 0.75rem 1rem;
      list-style: none;
      border-radius: 0.75rem;
      background-color: var(--cs-brand-primary-light);
      font-size: 0.9375rem;
      color: var(--cs-text-body);
    }

    &__header-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    // Location Selection
    &__location {
      padding: 1rem;
      border-bottom: 1px solid var(--cs-input-default-border);
    }


    &__required {
      color: var(--red-500);
    }

    &__kind-group {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
      gap: 0.75rem;
    }

    &__kind-card,
    &__fixed-card {
      position: relative;
      display: flex;
      gap: 0.75rem;
      padding: 0.875rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.625rem;
      background-color: var(--cs-input-default-background);
      color: var(--cs-text-body);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        background-color 0.15s ease;

      &:hover {
        border-color: var(--cs-brand-primary);
      }

      &:focus-within {
        box-shadow: 0 0 0 2px var(--cs-brand-primary-light);
      }

      &--selected {
        border-color: var(--cs-brand-primary);
        background-color: var(--cs-brand-primary-light);
      }
    }

    &__kind-card {
      flex-direction: column;
      align-items: flex-start;
      padding-right: 2.25rem;
    }

    &__kind-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.5rem;
      background-color: var(--cs-input-default-background);
      color: var(--cs-brand-primary);
    }

    &__kind-text,
    &__fixed-text {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__kind-title,
    &__fixed-name {
      font-weight: 600;
      font-size: 0.9rem;
    }

    &__kind-desc,
    &__fixed-address {
      font-size: 0.8rem;
      opacity: 0.75;
    }

    &__fixed-list-title {
      margin: 1rem 0 0;
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--cs-text-heading);
    }

    &__fixed-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    &__fixed-card {
      align-items: center;
    }

    &__radio-input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    &__radio-dot {
      flex-shrink: 0;
      width: 1.125rem;
      height: 1.125rem;
      border: 1.5px solid var(--cs-input-default-border);
      border-radius: 50%;
      background-color: var(--cs-input-default-background);
    }

    &__kind-card &__radio-dot {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
    }

    &__kind-card--selected &__radio-dot,
    &__fixed-card--selected &__radio-dot {
      border: 5px solid var(--cs-brand-primary);
    }

    &__location-select {
      width: 100%;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.375rem;
      background-color: var(--cs-input-default-background);
      color: var(--cs-input-default-text);
      font-size: 0.9rem;
      cursor: pointer;

      &:focus {
        outline: none;
        border-color: var(--cs-brand-primary);
        box-shadow: 0 0 0 2px var(--cs-brand-primary-light);
      }
    }

    &__kind-group + &__location-select {
      margin-top: 0.75rem;
    }

    &__no-slots {
      padding: 1rem;
      text-align: center;
      color: var(--cs-text-body);
      opacity: 0.8;
    }

    &__choose-location {
      padding: 1.5rem 1rem;
      text-align: center;
      color: var(--cs-text-body);
      // background: var(--cs-background-secondary, #f9fafb);
      border-radius: var(--cs-border-radius, 0.5rem);
      font-size: 0.9rem;
    }

    &__custom-address {
      margin-top: 0.75rem;
    }

    &__address-error {
      margin-top: 0.5rem;
      color: var(--cs-error, #dc2626);
      font-size: 0.875rem;
      font-weight: 500;
    }

    &__month {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 1rem 0 1rem;
    }

    &__month-label {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--cs-input-default-text);
      text-transform: capitalize;
    }

    &__month-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__today-button {
      padding: 0.375rem 0.75rem;
      border-radius: 0.5rem;
      border: 1px solid var(--cs-input-default-border);
      background: var(--cs-input-default-background);
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--cs-input-default-text);
      cursor: pointer;
      white-space: nowrap;
      box-shadow:
        0 0 rgba(0, 0, 0, 0),
        0 0 rgba(0, 0, 0, 0),
        0 1px 2px 0 rgba(0, 0, 0, 0.05);

      &:hover:not(:disabled) {
        background: var(--cs-choice-default-background);
        border-color: var(--cs-choice-hover-border);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &__month-nav {
      display: flex;
      gap: 0.25rem;
    }

    &__nav {
      display: flex;
      gap: 0.5rem;
    }

    &__nav-button {
      width: 2.25rem;
      height: 2.25rem;
      border-radius: 0.5rem;
      border: 1px solid var(--cs-input-default-border);
      background: var(--cs-input-default-background);
      color: var(--cs-input-default-text);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow:
        0 0 rgba(0, 0, 0, 0),
        0 0 rgba(0, 0, 0, 0),
        0 1px 2px 0 rgba(0, 0, 0, 0.05);

      &:hover:not(:disabled) {
        background: var(--cs-choice-default-background);
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      &--prev {
        .icon {
          transform: rotate(270deg);
        }
      }
      &--next {
        .icon {
          transform: rotate(90deg);
        }
      }
    }

    &__address-required-prompt {
      margin: 0;
      padding: 1.5rem 1rem;
      font-size: 1.375rem;
      font-weight: 600;
      line-height: 1.35;
      color: var(--cs-input-default-text);
      text-align: center;
    }

    &__calendar-panel {
      margin-top: 0.5rem;
      padding: 1rem;
      border-radius: 0.5rem;
      // Theme surface, so the theme's text colour stays readable on dark designs
      background: var(--cs-input-default-background);
      border: 1px solid var(--cs-input-default-border);

      .scheduling__calendar-view {
        border-radius: 0.5rem;
        background: var(--cs-input-default-background);
      }
    }

    &__confirm {
      display: flex;
      flex-direction: column;
      gap: 0;
      border-top: 1px solid var(--cs-input-default-border);
    }






    &__confirm-details {
      padding: 1.25rem 1.25rem 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }


    &__confirm-contact {
      margin: 0;
      padding: 0;
      border: none;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      min-width: 0;

      &:disabled {
        opacity: 0.85;
        cursor: not-allowed;
      }

      // The global disabled input style uses fixed slate colours; keep the theme once locked
      :deep(.sf-el input:disabled) {
        background-color: var(--cs-input-default-background);
        border-color: var(--cs-input-default-border);
        color: var(--cs-input-default-text);
      }
    }

    &__confirm-error {
      margin: 0;
      color: var(--cs-error, #dc2626);
      font-size: 0.875rem;
    }

    &__confirm-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }

    &__step-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 1rem;
    }

    // Same horizontal inset as the confirm details, so the back button lines up in both steps
    &__confirm-actions--step {
      margin-top: 0;
      padding: 0 1.25rem 1.5rem;
    }

    &__confirm-submit {
      margin-left: auto;
    }

    &__calendar-panel-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    &__calendar-panel-title {
      margin: 0 0 0.35rem;
      font-size: 1rem;
      font-weight: 600;
      color: var(--cs-input-default-text);
    }

    &__calendar-panel-hint {
      margin: 0;
      font-size: 0.875rem;
      color: var(--cs-input-default-text);
      opacity: 0.85;
    }

    &__calendar-panel-close {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      flex-shrink: 0;
      padding: 0.25rem;
      border: none;
      background: none;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--cs-input-default-text);
      cursor: pointer;
      text-decoration: underline;
    }

    &__pin-cards {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1rem;
    }

    &__no-coverage-notice {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      line-height: 1.35;
      color: var(--cs-input-default-text);
      text-align: center;
    }

    &__pin-card {
      padding: 1.25rem 1rem;
      border: 1px solid var(--cs-choice-default-border);
      border-radius: 0.5rem;
      background: var(--cs-choice-default-background);
      color: var(--cs-choice-default-text);
      box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);

      &--unavailable {
        opacity: 0.55;
        pointer-events: none;
        filter: grayscale(0.85);
      }
    }

    &__pin-card-body {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1rem;

      @include md-down {
        flex-direction: column;
        align-items: stretch;
      }
    }

    &__pin-card-slot {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.2rem;
      text-align: left;
    }

    &__pin-card-intro {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 500;
      line-height: 1.35;
      color: var(--cs-choice-default-text);
      opacity: 0.7;
    }

    &__pin-card-date {
      margin: 0.25rem 0 0;
      font-size: 1.125rem;
      font-weight: 700;
      line-height: 1.3;
      color: var(--cs-choice-default-text);

      &--unavailable {
        opacity: 0.7;
        font-weight: 500;
      }
    }

    &__pin-card-time {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 700;
      line-height: 1.3;
      color: var(--cs-choice-default-text);
    }

    &__pin-card-book {
      flex: 0 0 auto;
      width: auto;
      min-width: 11rem;
      max-width: 14rem;

      @include md-down {
        width: 100%;
        max-width: none;
      }

      &--cta :deep(.btn) {
        width: 100%;
        padding-top: 0.875rem;
        padding-bottom: 0.875rem;
        border: none;
        border-radius: 0.35rem;
        background-color: var(--cs-button-default-background);
        color: var(--cs-button-default-text);
        font-size: 0.9375rem;
        font-weight: 700;
        line-height: 1.25;
        box-shadow: none;
        white-space: normal;
        text-align: center;

        &:hover:not(:disabled) {
          background-color: var(--cs-button-hover-background);
          color: var(--cs-button-hover-text);
        }

        &:disabled {
          opacity: 0.65;
        }
      }
    }




    &__slot-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      width: 100%;
      padding: 1rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.75rem;
      background-color: var(--cs-input-default-background);
      color: var(--cs-text-body);
      text-align: left;
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

      &:hover:not(:disabled) {
        border-color: var(--cs-brand-primary);
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--cs-brand-primary-light);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &__slot-badge {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 0.75rem;
      background-color: var(--cs-brand-primary-light);
      color: var(--cs-brand-primary);
      line-height: 1.1;
    }

    &__slot-badge-month {
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    &__slot-badge-day {
      font-size: 1.5rem;
      font-weight: 700;
    }

    &__slot-text {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__slot-title {
      font-size: 1rem;
      font-weight: 700;
      color: var(--cs-text-heading);
      text-transform: capitalize;
    }

    &__slot-subtitle {
      font-size: 0.8125rem;
      opacity: 0.7;
    }

    &__slot-time {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      flex-shrink: 0;
      font-weight: 700;
      color: var(--cs-brand-primary);
    }

    &__slot-chevron {
      flex-shrink: 0;
      transform: rotate(90deg);
    }

    &__pin-card-choose-own {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      align-self: center;
      margin: 0.5rem 0 0;
      padding: 0.35rem;
      border: none;
      background: none;
      color: var(--cs-input-default-text);
      font-size: 0.9375rem;
      font-weight: 500;
      cursor: pointer;
      text-decoration: none;

      .icon {
        transform: rotate(90deg);
      }

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    }

    &__picker {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: 0.5rem;
      @include md-down {
        flex-direction: column;
      }
    }

    &__calendar {
      flex: 1 1 0;
      min-width: 0;
      padding: 1rem;
      box-sizing: border-box;
    }

    &__times {
      // Fixed width: sized to its content, the column (and so the calendar) shifted per selected date label
      flex: 0 0 13rem;
      width: 13rem;
      min-width: unset;
      max-width: 100vw;

      @include md-down {
        flex-basis: auto;
        width: 100%;
      }
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 1rem 1rem 1rem 1rem;
      &-header {
        @include fontSize('md');
        @include md-down {
          text-align: center;
        }
      }
    }

    &__weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 0.25rem;
      margin-bottom: 0.25rem;
      padding: 0;
      font-weight: 600;
      color: var(--cs-color-body);
      opacity: 0.9;
      text-align: center;
      letter-spacing: 0.02em;
      font-size: 0.85rem;
    }

    .scheduling__weekday {
      font-size: 0.8rem;
    }

    &__days-container {
      position: relative;
      overflow: hidden;
      width: 100%;
      padding: 0 1px;
      --day-width: 100px;
      margin: 0 -0.75rem;
    }

    .scheduling__days {
      display: flex;
      overflow-x: auto;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      gap: 12px;
      padding: 12px;
    }

    .scheduling__days::-webkit-scrollbar {
      display: none;
    }

    &__day {
      flex: 0 0 auto;
      width: var(--day-width);
      padding: 1rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.5rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;

      &--has-slots {
        border-color: var(--cs-input-default-border);
        background-color: var(--cs-input-default-background);

        &:hover {
          background: var(--cs-input-default-border);
        }
      }

      &--empty {
        cursor: default;
        pointer-events: none;
        opacity: 0.5;
      }

      &--selected {
        background: var(--cs-input-default-border);
        border-color: var(--cs-brand-primary);
        outline: 3px solid var(--cs-brand-primary);
      }
    }

    &__day-number {
      font-size: 1.2rem;
      font-weight: bold;
      margin-bottom: 0.25rem;
    }

    &__day-slots {
      @include fontSize('xs');
      color: var(--cs-input-default-text);
    }

    &__day-empty {
      font-size: 0.9rem;
      color: var(--cs-input-default-text);
      opacity: 0.7;
    }

    &__time-slot {
      padding: 0.25rem 0.5rem;
      border: 1px solid var(--cs-text-body);
      opacity: 0.9;
      border-radius: 0.5rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;

      &--selected {
        background: var(--cs-input-default-background);
        border-color: var(--cs-brand-primary);
        outline: 3px solid var(--cs-brand-primary);
        opacity: 1;
      }

      &--disabled {
        opacity: 0.45;
        cursor: not-allowed;
        pointer-events: none;
        border-style: dashed;

        &:hover {
          background: transparent;
          border-color: var(--cs-text-body);
        }
      }

      // Builder-only variables (--bg-hover, --primary) don't exist in the funnel
      &:hover {
        background: var(--cs-choice-default-background);
        border-color: var(--cs-brand-primary);
      }
    }

    &__slot-count {
      font-size: 0.75rem;
      color: var(--cs-input-default-text);
      opacity: 0.7;
    }

    &__loading {
      text-align: center;
      padding: 2rem;
      color: var(--cs-input-default-text);
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__error {
      margin: 0.75rem 1rem 0;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      background-color: #fef2f2;
      border: 1px solid #fecaca;
      color: #b91c1c;
      font-size: 0.875rem;
      font-weight: 600;
      line-height: 1.4;
    }

    &__footer {
      padding: 1rem 1rem 1rem 1rem;
      border-top: 1px solid var(--cs-input-default-border);
      @include fontSize('md');
    }
  }

  .scheduling__days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.5rem;
    padding: 0;
    width: 100%;
    margin: 0 auto;
  }

  .scheduling__day-circle {
    aspect-ratio: 1/1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 500;
    background: transparent;
    color: var(--cs-input-default-text);
    cursor: pointer;
    transition: all 0.15s;
    position: relative;
    width: 100%;
    // Fills its grid cell, capped so circles stay the same size when the time list opens beside it
    max-width: 4.5rem;
    margin: 0 auto;
    height: auto;

    &--has-slots {
      background-color: var(--cs-choice-default-background);
      border: 1px solid var(--cs-choice-default-border);
    }

    // Only pickable days; the selected day keeps its own style
    &--has-slots:not(.scheduling__day-circle--selected):hover {
      border-color: var(--cs-brand-primary);
      background-color: var(--cs-brand-primary-light);
    }

    &--selected {
      background: var(--cs-brand-primary);
      color: var(--cs-button-default-text);
      border-color: var(--cs-brand-primary);
      box-shadow: 0 0 0 2px var(--cs-brand-primary-light);
    }

    &--empty {
      color: var(--cs-input-default-text);
      background: transparent;
      cursor: default;
      pointer-events: none;
      opacity: 0.3;
    }

    &--placeholder {
      background: transparent;
      border: none;
      cursor: default;
      pointer-events: none;
    }

    // Today: bold number with a brand-coloured dot underneath
    &--today {
      font-weight: 700;

      &::after {
        content: '';
        position: absolute;
        bottom: 12%;
        left: 50%;
        width: 0.3rem;
        height: 0.3rem;
        border-radius: 50%;
        background: var(--cs-brand-primary);
        transform: translateX(-50%);
      }
    }

    // Keep today findable even when it has no slots left
    &--today.scheduling__day-circle--empty {
      opacity: 0.7;
    }

    // The filled selected circle already stands out; the dot would only crowd the number
    &--today.scheduling__day-circle--selected::after {
      display: none;
    }

    span {
      width: 100%;
      text-align: center;
    }
  }
</style>
